import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { resetSubmitExternalProviderInvoice } from "@academy-context/write/application/use-cases/external-provider/invoice-submission/submit-external-provider-invoice";
import { resetInitializeContract } from "@academy-context/write/application/use-cases/external-provider/subcontracting-contract-enable/enable-subcontracting-contract";
import { useExternalProviderDashboard } from "./hooks/use-external-provider-dashboard";
import { selectExternalProviderInvoiceSubmission } from "@academy-context/write/application/use-cases/external-provider/invoice-submission/selectors/external-provider-invoice-submission-selectors";
import { useContractInitialization } from "./hooks/use-contract-initialization";
import { ExternalProviderPortalSkeleton } from "./external-provider-portal.skeleton";
import { DashboardHeader } from "./components/dashboard-header.content";
import { ProgressStepper } from "./components/progress-stepper.content";
import { RequestDataSectionContainer } from "./components/request-data-section/request-data-section.container";
import { RequestDataReadOnly } from "./components/request-data-section/request-data-read-only.content";
import { InvoiceEditablePhase } from "./components/phases/invoice-editable-phase.content";
import { InvoiceReadOnlyPhase } from "./components/phases/invoice-readonly-phase.content";
import { EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS } from "@academy-context/shared/domain/types/enums/external-provider-batch-request";
import { isEvaluationSubmitted } from "@academy-context/read/domain/types/shared/training-item-evaluation";
import { ExternalProviderRepositoryProvider } from "./context/external-provider-repository.context";
import { SignedContractCard } from "./components/signed-contract-card.content";
import { UnsignedContractCard, UnsignableContractCard } from "./components/unsigned-contract-card.content";
import { ContractSignatureDialog } from "./components/contract-signature-dialog.content";
import { SUBCONTRACTING_CONTRACT_STATUS } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import { getPhaseFromStatus } from "./utils/get-phase-from-status";
import { DashboardPageLayout } from "@shared-kernel/primary/shared/layout/dashboard-page-layout";
import type { ExternalProviderDashboardVM } from "@shared-kernel/application/ports/external-provider/external-provider-repository";

interface Props {
  accessToken: string;
}

const ExternalProviderPortalContent = () => {
  const dispatch = useAppDispatch();
  const { data, fetching, error, refetch } = useExternalProviderDashboard();
  // Facture déposée : affichée sans attendre le rechargement du tableau de bord
  const { processing: invoiceSubmitProcessing, submittedInvoiceData } = useAppSelector(selectExternalProviderInvoiceSubmission);
  const {
    isDialogOpen,
    signatureLink,
    contractSignedOptimistically,
    handleContractInitialized,
    handleSignatureDone,
    handleCloseDialog,
    handleDeclined,
    handleError,
  } = useContractInitialization();

  const handleRequestDataSuccess = useCallback(() => {
    refetch();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [refetch]);

  // Facture déposée et lien de signature gardés après succès (exception au reset automatique) : remis à zéro en
  // quittant l'écran
  useEffect(
    () => () => {
      dispatch(resetSubmitExternalProviderInvoice());
      dispatch(resetInitializeContract());
    },
    [dispatch],
  );

  const renderContent = (data: ExternalProviderDashboardVM) => {
    // Determine current status (use optimistic update for invoice submission)
    const currentStatus =
      invoiceSubmitProcessing === "success" ? EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.INVOICE_TO_VALIDATE_BY_ADMIN : data.status;
    const phase = getPhaseFromStatus(currentStatus);

    const evaluationSubmitted = isEvaluationSubmitted(data);

    const isAfterRequestApproval = phase !== "review" && phase !== "pending_validation";
    const isContractPendingSignature =
      !data.request.subcontractingContract || data.request.subcontractingContract.status === SUBCONTRACTING_CONTRACT_STATUS.PENDING;
    const isContractSigned =
      contractSignedOptimistically || data.request.subcontractingContract?.status === SUBCONTRACTING_CONTRACT_STATUS.SIGNED;

    const showUnsignedContractCard = isAfterRequestApproval && !contractSignedOptimistically && isContractPendingSignature;
    const showSignedContractCard = isAfterRequestApproval && isContractSigned;
    // Null on requests issued before the legal representative was required: the contract cannot be signed
    const legalRepresentative = data.provider.legalRepresentative;

    const renderPhase = () => {
      switch (phase) {
        case "review":
          return <RequestDataSectionContainer data={data} onSubmitted={handleRequestDataSuccess} />;

        case "pending_validation":
          return <RequestDataReadOnly data={data} />;

        case "training_in_progress":
          return <RequestDataReadOnly data={data} />;

        case "invoice_editable":
          return <InvoiceEditablePhase data={data} currentStatus={currentStatus} onEvaluationSubmitted={refetch} />;

        case "invoice_readonly":
          return <InvoiceReadOnlyPhase data={data} onEvaluationSubmitted={refetch} optimisticInvoice={submittedInvoiceData} />;
      }
    };

    return (
      <>
        <ProgressStepper currentStatus={currentStatus} trainingEndDate={data.training.interval.end} />
        <DashboardHeader
          currentStatus={currentStatus}
          isEvaluationSubmitted={evaluationSubmitted}
          isInvoiceSubmitted={data.invoice !== null}
        />
        {showUnsignedContractCard &&
          (legalRepresentative ? (
            <UnsignedContractCard
              contract={data.request.subcontractingContract}
              legalRepresentative={legalRepresentative}
              providerEmail={data.provider.email}
              onContractInitialized={handleContractInitialized}
            />
          ) : (
            <UnsignableContractCard />
          ))}
        {renderPhase()}
        {showSignedContractCard && <SignedContractCard contract={data.request.subcontractingContract} />}
        <ContractSignatureDialog
          isOpen={isDialogOpen}
          signatureLink={signatureLink}
          onSignatureDone={handleSignatureDone}
          onClose={handleCloseDialog}
          onDeclined={handleDeclined}
          onError={handleError}
        />
      </>
    );
  };

  return (
    <DashboardPageLayout
      fetching={fetching}
      data={data}
      loadingComponent={<ExternalProviderPortalSkeleton />}
      errorStateProps={{
        title: "Erreur",
        description: error || "Une erreur est survenue lors du chargement des données.",
      }}
      emptyStateProps={{
        title: "Aucune donnée disponible",
      }}
      className="mt-8 flex flex-col justify-center space-y-6"
    >
      {renderContent}
    </DashboardPageLayout>
  );
};

export const ExternalProviderPortalContainer = ({ accessToken }: Props) => {
  return (
    <ExternalProviderRepositoryProvider accessToken={accessToken}>
      <ExternalProviderPortalContent />
    </ExternalProviderRepositoryProvider>
  );
};
